import {
  getSundayOfWeek,
  minutesFromMidnightToReadableTime,
  minutesFromMidnightToTime,
} from '../../../../utils/timeConverter';

const formatDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateShort = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

/**
 * Generate recurring dates for a 2-week alternating schedule, using per-day shift config.
 *
 * @param {Object} params
 * @param {Date|string} params.startDate - Start date (inclusive)
 * @param {Date|string} params.endDate - End date (inclusive)
 * @param {Object<string, {startTime: number, endTime: number, openings?: number}>} params.week1Shifts
 * @param {Object<string, {startTime: number, endTime: number, openings?: number}>} params.week2Shifts
 * @param {number} [params.maxShiftDurationMinutes=1200] - Default 20 hours
 * @returns {Array<Object>}
 */
const generateRecurringTwoWeekShiftDates = ({
  startDate,
  endDate,
  week1Shifts = {},
  week2Shifts = {},
  maxShiftDurationMinutes = 20 * 60,
}) => {
  if (!startDate || !endDate) return [];

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (start > end) return [];

  const minutesInDay = 24 * 60;

  const generateDatesForShifts = (shifts, isWeek1) => {
    const allDates = [];
    const firstSunday = getSundayOfWeek(start);
    const currentSunday = new Date(firstSunday);
    let weekNumber = 1;

    while (currentSunday <= end) {
      const isCurrentWeekPattern = (isWeek1 && weekNumber === 1) || (!isWeek1 && weekNumber === 2);

      if (isCurrentWeekPattern) {
        Object.keys(shifts || {}).forEach((dayIndexStr) => {
          const dayIndex = parseInt(dayIndexStr, 10);
          const shift = shifts?.[dayIndexStr];
          if (!shift) return;
          if (Number.isNaN(dayIndex) || dayIndex < 0 || dayIndex > 6) return;

          const date = new Date(currentSunday);
          date.setDate(date.getDate() + dayIndex);
          date.setHours(0, 0, 0, 0);

          if (date < start || date > end) return;

          const { startTime, endTime } = shift;
          if (startTime == null || endTime == null) return;
          if (typeof startTime !== 'number' || typeof endTime !== 'number') return;

          const duration =
            endTime >= startTime ? endTime - startTime : minutesInDay - startTime + endTime;
          if (duration === 0 || duration > maxShiftDurationMinutes) return;

          const openings = shift.openings ?? 1;

          const startTimeObj = minutesFromMidnightToTime(startTime);
          const endTimeObj = minutesFromMidnightToTime(endTime);
          const startTimeStr = minutesFromMidnightToReadableTime(startTime);
          const endTimeStr = minutesFromMidnightToReadableTime(endTime);

          const dateString = formatDate(date);
          const dateStringShort = formatDateShort(date);
          const timeRange = `${startTimeStr} - ${endTimeStr}`;
          const displayString = `${dateString} (${timeRange}): ${openings} opening${
            openings !== 1 ? 's' : ''
          }`;

          allDates.push({
            date,
            dateString,
            dateStringShort,
            startTime: startTimeObj,
            endTime: endTimeObj,
            startTimeMinutes: startTime,
            endTimeMinutes: endTime,
            timeRange,
            openings,
            displayString,
          });
        });
      }

      currentSunday.setDate(currentSunday.getDate() + 7);
      weekNumber = weekNumber === 1 ? 2 : 1;
    }

    return allDates;
  };

  const week1Dates = generateDatesForShifts(week1Shifts, true);
  const week2Dates = generateDatesForShifts(week2Shifts, false);
  return [...week1Dates, ...week2Dates].sort((a, b) => a.date - b.date);
};

export default generateRecurringTwoWeekShiftDates;
